/**
 * Serialization for the local secret state.
 *
 * `IdentityState` holds raw private keys as Uint8Arrays, which JSON would turn
 * into `{"0":12,...}` objects — recoverable, but silently 10x larger and easy
 * to get wrong. These functions make the encoding explicit.
 */
import {
  type IdentityState,
  type StoredSignedPreKey,
  fromB64,
  toB64,
} from '@vav/protocol';

interface SerializedKeyPair {
  priv: string;
  pub: string;
}

interface SerializedSignedPreKey {
  id: number;
  keys: SerializedKeyPair;
  sig: string;
  createdAt: number;
}

export interface SerializedIdentity {
  userId: string;
  deviceId: number;
  registrationId: number;
  identity: SerializedKeyPair;
  signedPreKey: SerializedSignedPreKey;
  previousSignedPreKey?: SerializedSignedPreKey;
  oneTimePreKeys: Record<string, SerializedKeyPair>;
  nextPreKeyId: number;
}

const pair = (kp: { priv: Uint8Array; pub: Uint8Array }): SerializedKeyPair => ({
  priv: toB64(kp.priv),
  pub: toB64(kp.pub),
});

const unpair = (kp: SerializedKeyPair) => ({ priv: fromB64(kp.priv), pub: fromB64(kp.pub) });

const spk = (k: StoredSignedPreKey): SerializedSignedPreKey => ({
  id: k.id,
  keys: pair(k.keys),
  sig: toB64(k.sig),
  createdAt: k.createdAt,
});

const unspk = (k: SerializedSignedPreKey): StoredSignedPreKey => ({
  id: k.id,
  keys: unpair(k.keys),
  sig: fromB64(k.sig),
  createdAt: k.createdAt,
});

export function serializeIdentity(state: IdentityState): SerializedIdentity {
  const oneTimePreKeys: Record<string, SerializedKeyPair> = {};
  for (const [id, keys] of Object.entries(state.oneTimePreKeys)) oneTimePreKeys[id] = pair(keys);
  return {
    userId: state.userId,
    deviceId: state.deviceId,
    registrationId: state.registrationId,
    identity: pair(state.identity),
    signedPreKey: spk(state.signedPreKey),
    previousSignedPreKey: state.previousSignedPreKey ? spk(state.previousSignedPreKey) : undefined,
    oneTimePreKeys,
    nextPreKeyId: state.nextPreKeyId,
  };
}

export function deserializeIdentity(s: SerializedIdentity): IdentityState {
  const oneTimePreKeys: IdentityState['oneTimePreKeys'] = {};
  for (const [id, keys] of Object.entries(s.oneTimePreKeys)) oneTimePreKeys[Number(id)] = unpair(keys);
  return {
    userId: s.userId,
    deviceId: s.deviceId,
    registrationId: s.registrationId,
    identity: unpair(s.identity),
    signedPreKey: unspk(s.signedPreKey),
    previousSignedPreKey: s.previousSignedPreKey ? unspk(s.previousSignedPreKey) : undefined,
    oneTimePreKeys,
    nextPreKeyId: s.nextPreKeyId,
  };
}
